import {
  Col,
  Radio,
  Row,
  Form,
  Select,
  Slider,
  Tooltip,
  Table,
  Tag,
  Typography,
  Pagination,
  RadioChangeEvent,
  DatePicker,
  Popover,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import {
  exportReleaseRankingCSV,
  exportReleaseRankingExcel,
  fetchArtistByName,
  fetchLabelByName,
  fetchReleaseRankingSlidersFilters,
} from "radar/Service/RadarService";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Loader, FieldSelect } from "shared/components";
import {
  RADAR_RELEASE_RANKING_HIGHLIGHTED_ARTISTS,
  RADAR_RELEASE_RANKING_FILTER,
  RADAR_RELEASE_RANKING_INDEX,
} from "shared/constants/LocalStorageConsts";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { getLocaleDatePickerByI18n } from "shared/helpers/getLocaleDatePickerByI18n";
import { I18NColumn } from "../I18nColumn";
import { ListColumn } from "../ListColumn";
import { InfoCircleOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import Avatar from "antd/lib/avatar/avatar";
import moment from "moment";
import RadioButtonRankingOptions from "../radio-button-ranking-options/RadioButtonRankingOptions";
import { ptBRFormat } from "shared/helpers/BRLFormat";
import { FileType, downloadFile } from "shared/helpers/download-file";
import { ReactComponent as ExportIcon } from "shared/assets/images/cloud-download.svg";
import {
  ReleaseRankingIndex,
  clearReleasesRanking,
  getReleaseRanking,
} from "explorer/reducers/rankings";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";

const { RangePicker } = DatePicker;

const defaults: any = {
  defaultForm: {
    labels: [],
    genreStrm: [],
    country: [],
    artistIdStrm: [],
    track: [1, 100],
    releaseAgeTimeFrame: null,
    releaseType: null,
  },
  defaultFilter: {
    labels: [],
    genreStrm: [],
    country: [],
    artistIdStrm: [],
    releaseAgeTimeFrame: null,
    releaseType: null,
    offset: 0,
    limit: 50,
  },
};

const initial = () => {
  const savedFilter = localStorage.getItem(RADAR_RELEASE_RANKING_FILTER);

  const highlightedArtists = localStorage.getItem(
    RADAR_RELEASE_RANKING_HIGHLIGHTED_ARTISTS
  );
  const defaultArtists: any = highlightedArtists
    ? JSON.parse(highlightedArtists)
    : [];

  if (savedFilter) {
    const parsed = JSON.parse(savedFilter);

    const { trackEnd, trackInit, ...rest } = parsed;
    return {
      defaultForm: {
        ...rest,
        track: [trackInit, trackEnd],
        releaseAgeTimeCustom: parsed.releaseAgeTimeCustom
          ? [
              moment(parsed.releaseAgeTimeCustom[0]),
              moment(parsed.releaseAgeTimeCustom[1]),
            ]
          : null,
      },
      defaultFilter: parsed,
      defaultArtists,
      defaultRangeFilter: parsed.defaultRangeFilter
        ? parsed.defaultRangeFilter
        : "week",
    };
  }

  return {
    ...defaults,
    defaultArtists,
    defaultRangeFilter: "week",
  };
};

const timeFrameOptions = [
  "oneWeek",
  "twoWeek",
  "oneMonth",
  "threeMonth",
  "sixMonth",
  "oneYear",
  "twoYear",
  "threeYear",
  "fourYear",
  "fiveYear",
  "tenYear",
  "2000s",
  "90s",
  "80s",
  "70s",
  "60s",
  "50s",
  "custom",
];

const TIME_FRAME_OPTION_CUSTOM = 17;

const rankingOptionsWithTop: Array<string> = [
  ReleaseRankingIndex.Top200,
  ReleaseRankingIndex.Top1000,
];

const formatDate = (date: string) => date && moment(date).format("DD/MM/YYYY");

const ReleasesRanking: React.FC<RouteComponentProps> = (props) => {
  const savedIndex = localStorage.getItem(
    RADAR_RELEASE_RANKING_INDEX
  ) as ReleaseRankingIndex;
  const { defaultFilter, defaultForm, defaultArtists, defaultRangeFilter } =
    initial();
  const { t, i18n } = useTranslation("radar");
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [filter, setFilter] = useState<any>(defaultFilter);
  const [releaseAgeTimeFrameFieldValue, setReleaseAgeTimeFrameFieldValue] =
    useState<any>(defaultFilter?.releaseAgeTimeFrame ?? "");
  const [loadingArtist, setLoadingArtist] = useState<boolean>(false);
  const [enableExport, setEnableExport] = useState<boolean>(false);
  const [loadingLabels, setLoadingLabels] = useState<boolean>(false);
  const [labelOptions, setLabelOptions] = useState<any>([]);
  const [slidersFilters, setSlidersFilters] = useState<any>();
  const [loadingLocal, setLoadingLocal] = useState<boolean>(false);
  const [artistsOptions, setArtistsOptions] = useState<any>(
    defaultArtists.map(({ artistIdStrm, artistName, imagesSpotify }: any) => ({
      id: artistIdStrm,
      name: artistName,
      image: (imagesSpotify && imagesSpotify[2]?.url) || "",
    }))
  );
  const [searchedArtists, setSearchedArtists] = useState<any>([]);
  const [rankingIndex, setRankingIndex] = useState<ReleaseRankingIndex>(
    savedIndex ? savedIndex : ReleaseRankingIndex.Popularity
  );
  const [rangeFilter, setRangeFilter] = useState<string>(
    defaultRangeFilter ? defaultRangeFilter : "week"
  );

  const { countries, countryOptions } = useSelector(
    (state: any) => state.countries
  );

  const { genres, genreOptions } = useSelector((state: any) => state.genres);
  const { releasesRanking, loading } = useSelector(
    (state: any) => state.rankings
  );

  const colunas: ColumnsType<any> = [
    {
      dataIndex: "position",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ReleasesRanking.release"),
      dataIndex: "releaseName",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ReleasesRanking.releaseType"),
      dataIndex: "releaseType",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ReleasesRanking.labelName"),
      dataIndex: "labelName",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ReleasesRanking.ArtistName"),
      dataIndex: "artistNames",
      width: "5%",
      align: "center",
      render: (value) => (
        <ListColumn
          listRow={value ? [...value].sort() : []}
          label={t("pages.ReleasesRanking.ArtistName")}
          t={t}
        />
      ),
    },
    {
      title: t("pages.ArtistsRanking.country"),
      dataIndex: "country",
      width: "10%",
      align: "center",
      render: (value) => (
        <I18NColumn
          listRow={value ? [...value].sort() : []}
          label={t("pages.ArtistsRanking.country")}
          i18nFunction={(country: string) =>
            getI18nCoutry(country, i18n, countries)
          }
          t={t}
        />
      ),
    },
    {
      title: t("pages.ArtistsRanking.genres"),
      dataIndex: "genresStrm",
      width: "10%",
      align: "center",
      render: (value) => (
        <I18NColumn
          listRow={value ? [...value].sort() : []}
          label={t("pages.ArtistsRanking.genres")}
          i18nFunction={(genre: string) => getI18nGenre(genre, i18n, genres)}
          t={t}
        />
      ),
    },
    {
      title: rankingOptionsWithTop.includes(rankingIndex)
        ? t("pages.ReleasesRanking.popularityTableWithVariation")
        : t("pages.ReleasesRanking.popularityTable"),
      width: "5%",
      render: (value) => {
        return (
          <>
            {value.popularityIndex}
            {value.positionDif !== 0 &&
              value.positionDif !== null &&
              rankingOptionsWithTop.includes(rankingIndex) && (
                <>
                  {value.positionDif > 0 ? (
                    <span style={{ color: "green", fontWeight: "bold" }}>
                      (+{value.positionDif})
                    </span>
                  ) : (
                    <span style={{ color: "red", fontWeight: "bold" }}>
                      ({value.positionDif})
                    </span>
                  )}
                </>
              )}
          </>
        );
      },
    },
    {
      title: t("pages.ReleasesRanking.trackCount"),
      dataIndex: "trackCnt",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ReleasesRanking.ageInDays"),
      dataIndex: "releaseDays",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
  ];

  const getArtists = async (artistNameTerm: string) => {
    if (artistNameTerm.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "")) {
      setLoadingArtist(true);
      setArtistsOptions([]);
      const response = await fetchArtistByName({ artistNameTerm });
      setSearchedArtists(response);
      setLoadingArtist(false);
    }
  };

  const getLabels = async (term: string) => {
    const cleanTerm = term.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "");
    if (cleanTerm) {
      setLoadingLabels(true);
      setLabelOptions([]);
      const response: any = await fetchLabelByName(cleanTerm);
      setLabelOptions(
        response?.map((item: any) => ({ label: item, value: item }))
      );
      setLoadingLabels(false);
    }
  };

  const onDeleteClick = () => {
    dispatch(clearReleasesRanking());
    form.setFieldsValue(defaults.defaultForm);
    setFilter(defaults.defaultFilter);
    setReleaseAgeTimeFrameFieldValue("");
  };

  const onChangeIndex = (evt: any) => {
    const saveRankingIndex = evt.target.value;
    localStorage.setItem(RADAR_RELEASE_RANKING_INDEX, saveRankingIndex);
    setRankingIndex(saveRankingIndex);
  };

  const onChangeArtist = async (value: any) => {
    const highlightedArtists = localStorage.getItem(
      RADAR_RELEASE_RANKING_HIGHLIGHTED_ARTISTS
    );
    const artist: any = searchedArtists.find((opt: any) =>
      value.includes(opt.artistIdStrm)
    );
    if (highlightedArtists) {
      const parsed = JSON.parse(highlightedArtists);
      localStorage.setItem(
        RADAR_RELEASE_RANKING_HIGHLIGHTED_ARTISTS,
        JSON.stringify([...parsed, artist])
      );
    } else {
      localStorage.setItem(
        RADAR_RELEASE_RANKING_HIGHLIGHTED_ARTISTS,
        JSON.stringify([artist])
      );
    }
  };

  const onFinishFilter = () => {
    const {
      labels,
      genreStrm,
      country,
      artistIdStrm,
      releaseType,
      releaseAgeTimeFrame,
      track,
      releaseAgeTimeCustom,
    } = form.getFieldsValue(true);
    const [trackInit, trackEnd] = track;
    const saveFilter = {
      ...defaultFilter,
      country,
      labels,
      genreStrm,
      artistIdStrm,
      releaseType,
      trackEnd,
      trackInit,
      releaseAgeTimeFrame,
      releaseAgeTimeCustom,
    };
    dispatch(clearReleasesRanking());
    setArtistsOptions([]);
    localStorage.setItem(
      RADAR_RELEASE_RANKING_FILTER,
      JSON.stringify(saveFilter)
    );
    setFilter(saveFilter);
  };

  const getFormattedCustomTimeFrameFromForm = (formFilter: any) => {
    const isReleaseAgeCustom =
      formFilter.releaseAgeTimeFrame === TIME_FRAME_OPTION_CUSTOM;
    if (formFilter?.releaseAgeTimeCustom)
      return {
        releaseAgeCustomInit: isReleaseAgeCustom
          ? formFilter?.releaseAgeTimeCustom[0]
          : null,
        releaseAgeCustomEnd: isReleaseAgeCustom
          ? formFilter?.releaseAgeTimeCustom[1]
          : null,
      };

    return {
      releaseAgeCustomInit: null,
      releaseAgeCustomEnd: null,
    };
  };

  const onChangeRangeFilter = (evt: RadioChangeEvent) => {
    setRangeFilter(evt.target.value);
    const savedFilter = localStorage.getItem(RADAR_RELEASE_RANKING_FILTER);
    const savedFilteParsed = JSON.parse(savedFilter ? savedFilter : "{}");

    localStorage.setItem(
      RADAR_RELEASE_RANKING_FILTER,
      JSON.stringify({
        ...savedFilteParsed,
        defaultRangeFilter: evt.target.value,
      })
    );
  };

  const enableRangeFilter = (currentRanking: string) =>
    rankingOptionsWithTop.includes(currentRanking);

  const downloadRankingReportCSV = async () => {
    setLoadingLocal(true);
    const releaseAgeTimeFrameParams =
      getFormattedCustomTimeFrameFromForm(filter);
    const combinedFilter = {
      ...filter,
      sortMode: rankingIndex,
      timeFrame: enableRangeFilter(rankingIndex) ? rangeFilter : null,
      ...releaseAgeTimeFrameParams,
    };
    const data: any = await exportReleaseRankingCSV(combinedFilter);
    downloadFile(data, "releases-ranking.csv", FileType.Csv);
    setLoadingLocal(false);
    setEnableExport(false);
  };

  const downloadRankingReportExcel = async () => {
    setLoadingLocal(true);
    const releaseAgeTimeFrameParams =
      getFormattedCustomTimeFrameFromForm(filter);
    const combinedFilter = {
      ...filter,
      sortMode: rankingIndex,
      timeFrame: enableRangeFilter(rankingIndex) ? rangeFilter : null,
      ...releaseAgeTimeFrameParams,
    };
    const data: any = await exportReleaseRankingExcel(combinedFilter);
    downloadFile(data, "releases-ranking.xlsx", FileType.Xlsx);
    setLoadingLocal(false);
    setEnableExport(false);
  };

  useEffect(() => {
    dispatch(
      getReleaseRanking({
        ...getFormattedCustomTimeFrameFromForm(filter),
        filter,
        rankingIndex,
        timeFrame: enableRangeFilter(rankingIndex) ? rangeFilter : null,
      })
    );
  }, [rankingIndex, filter, rangeFilter, dispatch]);

  useEffect(() => {
    const getSliders = async () => {
      const response: any = await fetchReleaseRankingSlidersFilters();
      setSlidersFilters(response);
      defaults.defaultForm.track = [0, response?.tracksCount || 0];
      defaults.defaultForm.releaseAgeEnd = response?.ageCount || 0;

      form.setFieldsValue({
        track: [1, response?.tracksCount],
      });
    };
    getSliders();
  }, [form]);

  useEffect(() => {
    setArtistsOptions(
      searchedArtists.map(
        ({ artistIdStrm, artistName, imagesSpotify }: any) => ({
          id: artistIdStrm,
          name: artistName,
          image: (imagesSpotify && imagesSpotify[2]?.url) || "",
        })
      )
    );
  }, [searchedArtists]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Ranking.S4L_RANKING_RELEASES,
    });
  }, []);

  return (
    <div className="radar-home">
      <Row style={{ marginTop: 16 }}>
        <Col style={{ textAlign: "center" }}>
          <h1 className="ranking-title">{t("pages.ArtistsRanking.title")}</h1>
        </Col>
      </Row>
      <Row style={{ marginTop: 16 }}>
        <Col style={{ textAlign: "center" }}>
          <RadioButtonRankingOptions value="release" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} className="conteudo-home">
          <Form
            form={form}
            initialValues={defaultForm}
            onFinish={onFinishFilter}
            className="filtro"
            layout="vertical"
          >
            <Row gutter={24}>
              <Col xs={5}>
                <Form.Item
                  name="labels"
                  label={t("pages.ReleasesRanking.label")}
                >
                  <FieldSelect
                    mode="multiple"
                    filterOption={false}
                    onSearch={debounce(getLabels, 1000)}
                    notFoundContent={
                      <Loader.NotFoundLoader loading={loadingLabels} />
                    }
                    allowClear
                    dataList={labelOptions}
                    placeholder={t("pages.ArtistsRanking.selectHere")}
                    showSelectAll={false}
                  />
                </Form.Item>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="genreStrm"
                  label={t("pages.ArtistsRanking.genre")}
                >
                  <FieldSelect
                    mode="multiple"
                    allowClear
                    dataList={genreOptions}
                    placeholder={t("pages.ArtistsRanking.selectHere")}
                  />
                </Form.Item>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="country"
                  label={t("pages.ReleasesRanking.country")}
                >
                  <Select
                    mode="multiple"
                    options={countryOptions}
                    filterOption={(input: string, option: any) =>
                      option?.label
                        ?.toUpperCase()
                        ?.includes(input.toUpperCase())
                    }
                    maxTagCount="responsive"
                    placeholder={t("pages.ArtistsRanking.selectHere")}
                  />
                </Form.Item>
              </Col>
              <Col xs={5}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.artistIdStrm !== currentValues.artistIdStrm
                  }
                >
                  {({ getFieldValue }) => {
                    const artists = getFieldValue("artistIdStrm");
                    return (
                      <Form.Item
                        name="artistIdStrm"
                        label={t("pages.ReleasesRanking.artist")}
                      >
                        <Select
                          allowClear
                          filterOption={false}
                          tagRender={tagRender}
                          onSearch={debounce(getArtists, 1000)}
                          onSelect={onChangeArtist}
                          // onChange={onChangeArtist}
                          style={{ width: "100% " }}
                          mode="multiple"
                          maxTagCount={artists?.length > 3 ? "responsive" : 99}
                          notFoundContent={
                            <Loader.NotFoundLoader loading={loadingArtist} />
                          }
                          placeholder={t("pages.ArtistsRanking.typeHere")}
                        >
                          {artistsOptions.map((artist: any) => (
                            <Select.Option
                              value={artist.id}
                              key={artist.id}
                              label={artist.name}
                            >
                              {artist.image && (
                                <Avatar
                                  style={{
                                    marginRight: "6px",
                                    width: 25,
                                    height: 25,
                                  }}
                                  src={artist.image}
                                />
                              )}
                              {artist.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col xs={4}>
                <div className="filter-button">
                  <span className="delete" onClick={onDeleteClick}>
                    {t("pages.ArtistsRanking.delete")}
                  </span>
                  <button className="apply" disabled={loading || loadingLocal}>
                    {t("pages.ArtistsRanking.filter")}
                  </button>
                </div>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="releaseType"
                  label={t("pages.ReleasesRanking.releaseType")}
                >
                  <Select
                    allowClear
                    placeholder={t("pages.ArtistsRanking.selectHere")}
                  >
                    <Select.Option value="album">
                      {t("pages.ReleasesRanking.album")}
                    </Select.Option>
                    <Select.Option value="single">
                      {t("pages.ReleasesRanking.single")}
                    </Select.Option>
                    <Select.Option value="compilation">
                      {t("pages.ReleasesRanking.compilation")}
                    </Select.Option>
                    <Select.Option value="all">
                      {t("pages.ReleasesRanking.all")}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="track"
                  label={t("pages.ReleasesRanking.trackNumber")}
                >
                  <Slider
                    min={1}
                    max={slidersFilters?.tracksCount || 1}
                    range
                    marks={{
                      1: {
                        style: {
                          color: "#fff",
                        },
                        label: <strong>1</strong>,
                      },
                      [slidersFilters?.tracksCount || 100]: {
                        style: {
                          color: "#fff",
                        },
                        label: <strong>{slidersFilters?.tracksCount}</strong>,
                      },
                    }}
                    tooltipPlacement="bottomLeft"
                  />
                </Form.Item>
              </Col>
              <Col xs={5}>
                <Form.Item
                  name="releaseAgeTimeFrame"
                  label={t("pages.ReleasesRanking.period")}
                >
                  <Select
                    allowClear
                    onChange={(value) =>
                      setReleaseAgeTimeFrameFieldValue(value)
                    }
                    placeholder={t("pages.ArtistsRanking.selectHere")}
                  >
                    {timeFrameOptions.map((option, index) => (
                      <Select.Option value={index}>
                        {t(`pages.TracksRanking.${option}`)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col md={7}>
                <Form.Item
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.releaseAgeTimeFrame !==
                    currentValues.releaseAgeTimeFrame
                  }
                  noStyle
                >
                  {({ getFieldValue }) => {
                    return getFieldValue("releaseAgeTimeFrame") ===
                      TIME_FRAME_OPTION_CUSTOM ? (
                      <Form.Item
                        name={"releaseAgeTimeCustom"}
                        label={t("pages.ReleasesRanking.setPeriod")}
                      >
                        <RangePicker
                          locale={getLocaleDatePickerByI18n(i18n.language)}
                          format="DD/MM/YYYY"
                        />
                      </Form.Item>
                    ) : null;
                  }}
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Row style={{ marginBottom: 16, marginTop: 16 }}>
        <Col
          xs={24}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <Row justify="center">
            <Radio.Group onChange={onChangeIndex} value={rankingIndex}>
              <Radio.Button value={ReleaseRankingIndex.Popularity}>
                <Tooltip
                  title={t("pages.ReleasesRanking.popularityExplaination")}
                >
                  {t("pages.ReleasesRanking.popularity")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={ReleaseRankingIndex.CurrentPopularity}>
                <Tooltip
                  title={t(
                    "pages.ReleasesRanking.currentPopularityExplaination"
                  )}
                >
                  {t("pages.ReleasesRanking.currentPopularity")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={ReleaseRankingIndex.Stability}>
                <Tooltip
                  title={t("pages.ReleasesRanking.stabilityExplaination")}
                >
                  {t("pages.ReleasesRanking.stability")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={ReleaseRankingIndex.Top200}>
                <Tooltip title={t("pages.ReleasesRanking.top200Explaination")}>
                  {t("pages.ReleasesRanking.top200")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={ReleaseRankingIndex.Top1000}>
                <Tooltip title={t("pages.ReleasesRanking.top1000Explaination")}>
                  {t("pages.ReleasesRanking.top1000")}
                </Tooltip>
              </Radio.Button>
            </Radio.Group>
          </Row>
          <Row
            justify="center"
            style={{
              visibility: enableRangeFilter(rankingIndex)
                ? "visible"
                : "hidden",
            }}
          >
            <Radio.Group
              defaultValue="week"
              value={rangeFilter}
              onChange={onChangeRangeFilter}
              style={{ marginBottom: 0 }}
            >
              <Radio.Button value={"week"}>
                {t("pages.ReleasesRanking.1w")}
              </Radio.Button>
              <Radio.Button
                value={"month"}
                disabled={
                  releaseAgeTimeFrameFieldValue < 2 &&
                  releaseAgeTimeFrameFieldValue !== ""
                }
              >
                {t("pages.ReleasesRanking.1m")}
              </Radio.Button>
              <Radio.Button
                value={"trimester"}
                disabled={
                  releaseAgeTimeFrameFieldValue < 3 &&
                  releaseAgeTimeFrameFieldValue !== ""
                }
              >
                {t("pages.ReleasesRanking.3m")}
              </Radio.Button>
              <Radio.Button
                value={"semester"}
                disabled={
                  releaseAgeTimeFrameFieldValue < 4 &&
                  releaseAgeTimeFrameFieldValue !== ""
                }
              >
                {t("pages.ReleasesRanking.6m")}
              </Radio.Button>
              <Radio.Button
                value={"year"}
                disabled={
                  releaseAgeTimeFrameFieldValue < 5 &&
                  releaseAgeTimeFrameFieldValue !== ""
                }
              >
                {t("pages.ReleasesRanking.1y")}
              </Radio.Button>
            </Radio.Group>
            <Popover
              content={t("pages.ReleasesRanking.setPeriodExplain")}
              placement="right"
              style={{ color: "white" }}
            >
              <InfoCircleOutlined
                style={{
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "10px",
                }}
              />
            </Popover>
          </Row>
        </Col>
      </Row>
      <Row justify="end" style={{ gap: 12 }}>
        {enableExport && (
          <div className="container-botoes-exportar">
            <button
              className="button-export"
              onClick={() => downloadRankingReportCSV()}
            >
              CSV
            </button>
            <button
              className="button-export"
              onClick={() => downloadRankingReportExcel()}
            >
              Excel
            </button>
          </div>
        )}
        <button
          className="botao-azul button-export"
          onClick={() => setEnableExport(!enableExport)}
        >
          {t("pages.ArtistProfile.export")}
          <ExportIcon className="download-icon" />
        </button>
      </Row>
      <Row style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Row style={{ marginBottom: 16 }}>
            <Col xs={12}>
              <Typography.Title
                level={4}
                style={{ color: "white", textAlign: "left" }}
              >
                {t("pages.ReleasesRanking.results", {
                  total: ptBRFormat(
                    releasesRanking[`${rankingIndex}`]?.total || 0
                  ),
                })}
              </Typography.Title>
            </Col>
            <Col xs={12}>
              <Typography.Title
                level={5}
                style={{ color: "white", textAlign: "right" }}
              >
                {t("pages.ArtistsRanking.lastUpdate")}
                <b>
                  {`${
                    formatDate(
                      releasesRanking[`${rankingIndex}`]?.releaseDate
                        ?.rankingDate
                    ) || ""
                  } `}
                </b>
              </Typography.Title>
            </Col>
          </Row>
          <Table
            columns={colunas}
            dataSource={releasesRanking[`${rankingIndex}`]?.releases || []}
            loading={loading || loadingLocal}
            pagination={false}
            rowKey={(record) => `${record?.releaseName}_${record?.labelName}`}
            scroll={{ x: 1300 }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Pagination
              style={{ alignSelf: "flex-end", margin: "16px" }}
              hideOnSinglePage={true}
              showSizeChanger={false}
              onChange={(page) => {
                const saveFilter = {
                  ...filter,
                  offset: page * 50 - 50,
                  limit: 50,
                };

                localStorage.setItem(
                  RADAR_RELEASE_RANKING_FILTER,
                  JSON.stringify(saveFilter)
                );
                setFilter(saveFilter);
              }}
              current={filter?.offset / 50 + 1}
              total={releasesRanking[`${rankingIndex}`]?.total}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

function tagRender(props: any) {
  const { label, closable, onClose } = props;
  const onPreventMouseDown = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3, marginBottom: 3 }}
    >
      {label}
    </Tag>
  );
}

export default ReleasesRanking;
